import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Layout, { Section } from "@/components/layout/Layout";
import JsonLd from "@/components/common/JsonLd";
import InsightArticle from "@/components/common/InsightArticle";
import DetailNotFound from "@/components/common/DetailNotFound";
import useOgMeta from "@/lib/useOgMeta";
import { api, API } from "@/lib/api";
import { ArrowLeft, ArrowUp, Download } from "lucide-react";

const SCROLL_TOP_THRESHOLD = 600;

export default function InsightDetail() {
    const { slug } = useParams();
    const [post, setPost] = useState(null);
    const [error, setError] = useState(null);
    const [showScrollTop, setShowScrollTop] = useState(false);

    useEffect(() => {
        // Reset on every slug change — otherwise a previously-loaded post
        // (or a previous "not found") stays on screen while the new slug's
        // request is in flight. `cancelled` guards against a stale response
        // (e.g. a slow request for the previous slug) landing after a newer
        // one already resolved, or after unmount.
        let cancelled = false;
        setPost(null);
        setError(null);
        api.get(`/insights/${slug}`)
            .then((r) => { if (!cancelled) setPost(r.data); })
            .catch(() => { if (!cancelled) setError("Not found"); });
        return () => { cancelled = true; };
    }, [slug]);

    useEffect(() => {
        const onScroll = () => setShowScrollTop(window.scrollY > SCROLL_TOP_THRESHOLD);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    // useOgMeta restores whatever was here before whenever `post` changes
    // (including to null, when the slug changes) — so a previous article's
    // og/twitter tags never linger through a loading window or onto an
    // invalid slug (DetailNotFound applies its own suppression for the
    // latter once it renders).
    const og = post ? `${process.env.REACT_APP_BACKEND_URL}/api/og/insights/${post.slug}` : undefined;
    useOgMeta(post ? {
        title: post.title,
        description: post.excerpt,
        image: og,
        type: "article",
        twitterCard: "summary_large_image",
        twitterImage: og,
    } : {});

    if (error) {
        return (
            <DetailNotFound title="Not found — Ventara" heading="This essay is not here."
                            backTo="/insights" backLabel="Back to Insights" />
        );
    }

    if (!post) {
        return (
            <Layout title="Loading — Ventara" description="Loading this essay.">
                <Section className="pt-40 pb-32"><p className="prose-body">Loading…</p></Section>
            </Layout>
        );
    }

    return (
        <Layout title={`${post.title} — Ventara`} description={post.excerpt}>
            <JsonLd id="article" data={{
                "@context": "https://schema.org",
                "@type": "Article",
                "headline": post.title,
                "description": post.excerpt,
                "image": post.cover_image ? [post.cover_image] : undefined,
                "articleSection": post.category,
                "datePublished": post.published_at,
                "dateModified": post.updated_at || post.published_at,
                "author": { "@type": "Organization", "name": "Ventara", "url": "https://iventara.com/" },
                "publisher": {
                    "@type": "Organization",
                    "name": "Ventara",
                    "logo": { "@type": "ImageObject", "url": "https://iventara.com/logo.png" }
                },
                "mainEntityOfPage": { "@type": "WebPage", "@id": `https://iventara.com/insights/${post.slug}` }
            }} />
            <JsonLd id="breadcrumb-insight" data={{
                "@context": "https://schema.org",
                "@type": "BreadcrumbList",
                "itemListElement": [
                    { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://iventara.com/" },
                    { "@type": "ListItem", "position": 2, "name": "Insights", "item": "https://iventara.com/insights" },
                    { "@type": "ListItem", "position": 3, "name": post.title, "item": `https://iventara.com/insights/${post.slug}` }
                ]
            }} />
            <Section className="pt-32 pb-10">
                <div className="flex items-center justify-between">
                    <Link to="/insights" data-testid="insight-back" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
                        <ArrowLeft className="h-4 w-4" /> Insights
                    </Link>
                    <a href={`${API}/insights/${post.slug}/pdf`} data-testid="insight-download-pdf"
                       className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
                        <Download className="h-4 w-4" /> Download PDF
                    </a>
                </div>
            </Section>

            <InsightArticle post={post} />

            {showScrollTop && (
                <button onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                        data-testid="insight-scroll-top" aria-label="Back to top"
                        className="fixed bottom-8 right-8 z-40 h-11 w-11 rounded-full bg-foreground text-background grid place-items-center shadow-lg hover:bg-foreground/90 transition-colors">
                    <ArrowUp className="h-4 w-4" />
                </button>
            )}
        </Layout>
    );
}
